// Handle strategem targeting
// Animation parameters are optional to maintain backwards compatibility
import { hasPositiveEffectById } from '../utils/strategemUtils';
import {
  usesCasterGeneralProjectileOrigin,
  getStrategemCasterGeneralPixelPosition,
} from '../config/casterGeneralStrategemProjectile';
import { KAMIKAZE_ANNOUNCE_DURATION_MS } from '../config/kamikazeAnnouncement';
import { DUPLICATE_TARGET_FADE_DURATION_MS } from '../config/duplicateStrategemAnimation';
import { UNETHICAL_TARGET_FADE_DURATION_MS } from '../config/unethicalStrategemAnimation';
import { BETRAYAL_TARGET_FADE_DURATION_MS } from '../config/betrayalStrategemAnimation';

/**
 * When `data-tutorial-overlay-strategem-restrict` is set (campaign tutorial_ui.json → CampaignBattleUI),
 * resolve the chosen slot's order id the same way as `modules/websocketHandler.js` and require it to be
 * in the allow-list. Prevents registering the missile animation + WebSocket send for invalid picks
 * (server already rejects, but the client was showing a "ghost" flight with no damage / no card spent).
 */
function isUnitAllowedByTutorialStrategemOverlay(animationConfig, targetType, targetZone, targetIndex, targetUnitId) {
  if (targetType !== 'unit' && targetType !== 'single unit') return true;
  if (!animationConfig?.gameState?.state || animationConfig.userId == null || animationConfig.strategemId == null) {
    return true;
  }
  if (typeof document === 'undefined' || !document.body) return true;
  const overlayAttr = document.body.getAttribute('data-tutorial-overlay-strategem-restrict');
  if (!overlayAttr) return true;
  let overlay;
  try {
    overlay = JSON.parse(overlayAttr);
  } catch {
    return true;
  }
  const sid = String(animationConfig.strategemId);
  const allowed = overlay[sid] ?? overlay[animationConfig.strategemId];
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  const allowedSet = new Set(allowed.map(a => Number(a)));
  const actingId = String(animationConfig.userId);
  const stateKeys = Object.keys(animationConfig.gameState.state);
  const targetPlayerKey =
    targetUnitId === 'opponent'
      ? stateKeys.find(id => String(id) !== actingId)
      : stateKeys.find(id => String(id) === actingId) ?? animationConfig.userId;
  if (targetPlayerKey == null) return false;
  const rawZ = targetZone != null ? String(targetZone) : '';
  const normalizedZone = rawZ.replace(/^opponent_/, '').replace(/^your_/, '');
  const row = animationConfig.gameState.state[targetPlayerKey]?.playedOrders?.[normalizedZone];
  const slot = Array.isArray(row) && targetIndex != null ? row[targetIndex] : null;
  const oid = slot && slot.id != null ? Number(slot.id) : null;
  if (oid == null || Number.isNaN(oid)) return false;
  return allowedSet.has(oid);
}

const targetStrategem = (
  targetType, 
  targetZone, 
  targetIndex, 
  targetUnitId, 
  targetInstanceId, 
  ws, 
  gameId, 
  setError, 
  setStrategemTargetMode, 
  setSelectedStrategemId, 
  setStrategemTargetInfo,
  // Animation parameters (optional)
  animationConfig = null
) => {
  console.log('🎯 TARGET STRATEGEM: Frontend function called with:', {
    targetType,
    targetZone,
    targetIndex,
    targetUnitId,
    targetInstanceId,
    wsReadyState: ws?.readyState,
    gameId,
    animationConfig: animationConfig ? {
      hasRegisterAnimation: !!animationConfig.registerAnimation,
      strategemInstanceId: animationConfig.strategemInstanceId,
      strategemId: animationConfig.strategemId
    } : null
  });
  
  if (!ws || ws.readyState !== 1) {
    console.log('🎯 TARGET STRATEGEM: WebSocket not ready, showing error');
    setError("Connection lost. Please refresh the page.");
    return;
  }

  // Tutorial overlay: when strategem is restricted to specific units only, reject general targeting
  if (targetType === 'general' && typeof document !== 'undefined' && animationConfig?.strategemId) {
    const overlayAttr = document.body.getAttribute('data-tutorial-overlay-strategem-restrict');
    if (overlayAttr) {
      try {
        const overlay = JSON.parse(overlayAttr);
        const strategemId = animationConfig.strategemId;
        const allowed = overlay[String(strategemId)] ?? overlay[strategemId];
        if (Array.isArray(allowed) && allowed.length > 0) {
          setError('This strategem can only target the indicated unit in this step');
          return;
        }
      } catch (_) {}
    }
  }

  if (
    animationConfig &&
    !isUnitAllowedByTutorialStrategemOverlay(animationConfig, targetType, targetZone, targetIndex, targetUnitId)
  ) {
    setError('This strategem can only target the indicated unit in this step');
    return;
  }

  // Register animations if animation config is provided
  console.log('🎬 TARGET STRATEGEM: Animation config check', {
    hasAnimationConfig: !!animationConfig,
    hasRegisterAnimation: !!animationConfig?.registerAnimation,
    hasStrategemInstanceId: !!animationConfig?.strategemInstanceId,
    strategemInstanceId: animationConfig?.strategemInstanceId,
    hasGameState: !!animationConfig?.gameState,
    hasUserId: !!animationConfig?.userId,
    userId: animationConfig?.userId
  });
  
  if (animationConfig?.registerAnimation && animationConfig?.strategemInstanceId) {
    const { registerAnimation, strategemInstanceId, gameState, userId } = animationConfig;
    
    console.log('🎬 TARGET STRATEGEM: Animation config passed check, gameState:', {
      hasState: !!gameState?.state,
      stateKeys: gameState?.state ? Object.keys(gameState.state) : [],
      userId
    });
    
    // Determine effect type: 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
    const strategemId = animationConfig.strategemId;
    const isPositiveEffect = strategemId && gameState ? hasPositiveEffectById(strategemId, gameState) : false;
    const effectType = isPositiveEffect ? 'heal' : 'damage';
    console.log(`🎬 TARGET STRATEGEM: Strategem ${strategemId} effectType: ${effectType} (isPositive: ${isPositiveEffect})`);

    const stateKeys = gameState?.state ? Object.keys(gameState.state) : [];
    const opponentAbsoluteId = stateKeys.find((id) => String(id) !== String(userId));
    
    // Debug: List all cards in hand with their instanceIds
    const allCards = document.querySelectorAll('[data-instance-id]');
    console.log('🎬 All cards with data-instance-id:', Array.from(allCards).map(el => ({
      id: el.getAttribute('data-instance-id'),
      left: el.getBoundingClientRect().left
    })));
    console.log('🎬 Looking for strategem instanceId:', strategemInstanceId);
    
    // Get card position NOW before it's removed from hand
    const cardElement = document.querySelector(`[data-instance-id="${strategemInstanceId}"]`);
    
    let fromPosition = null;
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      fromPosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      console.log('🎬 Found card at position:', fromPosition);
    } else {
      // Fallback to center-bottom of screen
      fromPosition = { x: window.innerWidth / 2, y: window.innerHeight - 100 };
      console.log('🎬 Card NOT found! Using fallback position:', fromPosition);
    }

    const strategemIdNum = strategemId != null ? Number(strategemId) : NaN;
    if (usesCasterGeneralProjectileOrigin(strategemIdNum)) {
      const fromGeneral = getStrategemCasterGeneralPixelPosition(userId, userId, false);
      if (fromGeneral) {
        fromPosition = fromGeneral;
        console.log('🎬 TARGET STRATEGEM: Caster-general projectile origin:', fromPosition);
      }
    }
    
    console.log('🎬 TARGET STRATEGEM: Registering animations for strategem', {
      strategemInstanceId,
      targetType,
      targetZone,
      targetIndex,
      fromPosition
    });

    // Determine player ID for target
    let targetPlayerId = 'opponent';
    if (targetType === 'general') {
      // For general targeting, use targetUnitId directly
      targetPlayerId = targetUnitId === 'friendly' ? 'your' : 'opponent';
    } else if (targetZone === 'front' || targetZone === 'flank') {
      targetPlayerId = 'your';
    } else if (targetZone?.startsWith('opponent_')) {
      targetPlayerId = 'opponent';
    } else if (targetZone?.startsWith('your_')) {
      targetPlayerId = 'your';
    }
    
    // ✅ FIX: Normalize targetZone to always include player prefix for consistent DOM lookup
    // This ensures the zone matches the data-slot-zone attributes in BattleUI
    let normalizedTargetZone = targetZone;
    if (targetType !== 'general') {
      // For unit/drone/zone targeting, normalize the zone
      if (targetZone === 'front' || targetZone === 'flank') {
        normalizedTargetZone = `${targetPlayerId}_${targetZone}`;
        console.log(`🎬 TARGET STRATEGEM: Normalized zone "${targetZone}" to "${normalizedTargetZone}" (playerId: ${targetPlayerId})`);
      } else if (targetZone && !targetZone.includes('_')) {
        // Zone without prefix - add player prefix
        normalizedTargetZone = `${targetPlayerId}_${targetZone}`;
        console.log(`🎬 TARGET STRATEGEM: Normalized zone "${targetZone}" to "${normalizedTargetZone}" (playerId: ${targetPlayerId})`);
      }
      // If zone already has prefix (opponent_ or your_), keep it as is
    }

    if (targetType === 'zone') {
      // For zone targeting, animate one ball per unit in the zone
      // Get units in the targeted zone
      const zoneUnits = getUnitsInZone(gameState, userId, targetZone);
      
      if (zoneUnits.length > 0) {
        console.log(`🎬 TARGET STRATEGEM: ZONE targeting path - ${zoneUnits.length} units in ${targetZone}`);
        
        zoneUnits.forEach((unit, idx) => {
          const animationKey = `${strategemInstanceId}-zone-${targetZone}-unit-${idx}-${Date.now()}`;
          // ✅ BUG FIX: Use unit.unit.instanceId (the actual unit object) or fallback to unit.instanceId
          // getUnitsInZone returns { unit, instanceId: unit.instanceId }, so we need to check both
          const targetInstanceId = unit.unit?.instanceId || unit.instanceId;
          if (!targetInstanceId) {
            console.error(`❌ BUG: targetInstanceId is missing for slot ${idx} in zone ${targetZone}!`, {
              unit,
              'unit.unit': unit.unit,
              'unit.unit.instanceId': unit.unit?.instanceId,
              'unit.instanceId': unit.instanceId
            });
          }
          // ✅ FIX: Normalize unit.zone (which might be 'front' or 'flank' without prefix)
          let normalizedUnitZone = unit.zone;
          if (unit.zone === 'front' || unit.zone === 'flank') {
            normalizedUnitZone = `${targetPlayerId}_${unit.zone}`;
          } else if (unit.zone && !unit.zone.includes('_')) {
            normalizedUnitZone = `${targetPlayerId}_${unit.zone}`;
          }
          
          // ✅ CRITICAL: Capture ghost unit BEFORE sending WebSocket message (same as single unit targeting)
          // This allows units to remain visible during animation even if backend destroys them immediately
          const ghostUnit = unit.unit ? { ...unit.unit } : null;
          
          registerAnimation({
            fromHand: { instanceId: strategemInstanceId, position: fromPosition },
            toSlot: { 
              zone: normalizedUnitZone, // ✅ Use normalized zone
              index: unit.index, 
              playerId: targetPlayerId 
            },
            targetType: 'unit',
            strategemId: animationConfig.strategemId,
            strategemInstanceId,
            targetInstanceId: targetInstanceId, // Include target unit's instanceId for death animation suppression
            ghostUnit, // ✅ Include ghost unit data for visual persistence (CRITICAL for zone-targeting strategems)
            effectType, // 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
            animationKey,
            strategemCasterPlayerId: userId,
          });
        });
      } else {
        // No units in zone, animate to zone center
        console.log(`🎬 TARGET STRATEGEM: Zone targeting - no units, animating to zone center ${targetZone}`);
        const animationKey = `${strategemInstanceId}-zone-${targetZone}-center-${Date.now()}`;
        registerAnimation({
          fromHand: { instanceId: strategemInstanceId, position: fromPosition },
          toSlot: { 
            zone: normalizedTargetZone, // ✅ Use normalized zone
            index: null, 
            playerId: targetPlayerId 
          },
          targetType: 'zone',
          strategemId: animationConfig.strategemId,
          strategemInstanceId,
          effectType, // 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
          animationKey,
          strategemCasterPlayerId: userId,
        });
      }
    } else if (targetType === 'unit') {
      // Single unit targeting
      const animationKey = `${strategemInstanceId}-unit-${targetZone}-${targetIndex}-${Date.now()}`;
      // Single unit targeting - animate black ball to specific slot
      
      // ✅ GHOST UNIT: Capture target unit data BEFORE sending WebSocket message
      // This allows the unit to remain visible until animation completes
      // ❌ SKIP ghost units for strategem 186 (Phase Traverse) - it repositions units, doesn't destroy them
      // Ghost units are only needed when units are destroyed/killed by strategems
      let ghostUnit = null;
      const strategemId = animationConfig.strategemId;
      const isRepositioningStrategem = strategemId === 186; // Phase Traverse
      
      if (!isRepositioningStrategem && gameState?.state) {
        // Determine which player's zone we're targeting
        const isTargetingOpponent = targetZone.startsWith('opponent_');
        const targetPlayerId2 = isTargetingOpponent 
          ? Object.keys(gameState.state).find(id => String(id) !== String(userId))
          : userId;
        const zoneKey = targetZone.replace('opponent_', '').replace('your_', '');
        
        const targetZoneUnits = gameState.state?.[targetPlayerId2]?.playedOrders?.[zoneKey] || [];
        const targetUnit = targetZoneUnits[targetIndex];
        
        if (targetUnit) {
          ghostUnit = { ...targetUnit };
        }
      }
      
      registerAnimation({
        fromHand: { instanceId: strategemInstanceId, position: fromPosition },
        toSlot: { 
          zone: normalizedTargetZone, // ✅ Use normalized zone (e.g., 'your_front' instead of 'front')
          index: targetIndex, 
          playerId: targetPlayerId 
        },
        targetType: 'unit',
        strategemId: animationConfig.strategemId,
        strategemInstanceId,
        targetInstanceId, // Include target unit's instanceId for death animation suppression
        ghostUnit, // ✅ Include ghost unit data for visual persistence (null for repositioning strategems)
        effectType, // 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
        animationKey,
        strategemCasterPlayerId: userId,
      });
    } else if (targetType === 'drone') {
      // Drone targeting
      const animationKey = `${strategemInstanceId}-drone-${targetZone}-${targetIndex}-${Date.now()}`;
      console.log(`🎬 TARGET STRATEGEM: Drone targeting - animating to drone ${targetIndex}`);
      
      registerAnimation({
        fromHand: { instanceId: strategemInstanceId, position: fromPosition },
        toSlot: { 
          zone: normalizedTargetZone, // ✅ Use normalized zone
          index: targetIndex, 
          playerId: targetPlayerId 
        },
        targetType: 'drone',
        strategemId: animationConfig.strategemId,
        strategemInstanceId,
        targetInstanceId, // drone tile DOM + damage FX (same as units)
        effectType, // 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
        animationKey,
        strategemCasterPlayerId: userId,
      });
    } else if (targetType === 'general') {
      // Strategem 220: no flying orbs — avatar +/- and × via useViralEchoHealFx (same as 185/188). One animation for two-phase onComplete only.
      if (animationConfig.strategemId === 220) {
        const animationKey = `${strategemInstanceId}-general-plague-blood-vfx-${Date.now()}`;
        registerAnimation({
          fromHand: { instanceId: strategemInstanceId, position: fromPosition },
          toSlot: {
            zone: 'general',
            index: null,
            playerId: 'your',
          },
          targetType: 'general',
          strategemId: animationConfig.strategemId,
          strategemInstanceId,
          effectType: 'heal',
          animationKey,
          targetGeneralPlayerId: userId,
          strategemCasterPlayerId: userId,
        });
      } else {
        // Regular general targeting (single animation)
        const animationKey = `${strategemInstanceId}-general-${targetPlayerId}-${Date.now()}`;
        console.log(`🎬 TARGET STRATEGEM: General targeting - animating to ${targetPlayerId} general`);

        const targetGeneralAbsoluteId =
          targetUnitId === 'friendly' ? userId : opponentAbsoluteId;

        registerAnimation({
          fromHand: { instanceId: strategemInstanceId, position: fromPosition },
          toSlot: { 
            zone: 'general', 
            index: null, 
            playerId: targetPlayerId 
          },
          targetType: 'general',
          strategemId: animationConfig.strategemId,
          strategemInstanceId,
          effectType, // 'heal' (yellow) for positive effects, 'damage' (black) for negative effects
          animationKey,
          targetGeneralPlayerId: targetGeneralAbsoluteId,
          strategemCasterPlayerId: userId,
        });
      }
    }
  }

  // Send strategem targeting action wrapped in standard play-turn envelope
  const message = {
    type: "play-turn",
    gameId,
    action: {
      type: "targetStrategem",
      targetType: targetType,
      targetZone: targetZone,
      targetIndex: targetIndex,
      targetUnitId: targetUnitId,
      targetInstanceId: targetInstanceId
    }
  };

  const sendAndResetTargeting = () => {
    console.log('🎯 TARGET STRATEGEM: Sending WebSocket message:', message);
    ws.send(JSON.stringify(message));

    console.log('🎯 TARGET STRATEGEM: Resetting targeting state');
    setStrategemTargetMode(false);
    setSelectedStrategemId(null);
    setStrategemTargetInfo(null);
  };

  const delayKamikazeServer =
    targetType === 'unit' && animationConfig?.strategemId === 314;
  const delayDuplicateServer =
    targetType === 'unit' && animationConfig?.strategemId === 313;
  const delayUnethicalServer =
    targetType === 'unit' && animationConfig?.strategemId === 315;
  const delayBetrayalServer =
    animationConfig?.strategemId === 341 &&
    (targetType === 'unit' || targetType === 'drone');
  /** Strategem 60: same overlay timing as Betrayal (units only). */
  const delayRotSpreadServer =
    animationConfig?.strategemId === 60 && targetType === 'unit';

  if (delayKamikazeServer) {
    window.setTimeout(sendAndResetTargeting, KAMIKAZE_ANNOUNCE_DURATION_MS);
  } else if (delayDuplicateServer) {
    window.setTimeout(sendAndResetTargeting, DUPLICATE_TARGET_FADE_DURATION_MS);
  } else if (delayUnethicalServer) {
    window.setTimeout(sendAndResetTargeting, UNETHICAL_TARGET_FADE_DURATION_MS);
  } else if (delayBetrayalServer || delayRotSpreadServer) {
    window.setTimeout(sendAndResetTargeting, BETRAYAL_TARGET_FADE_DURATION_MS);
  } else {
    sendAndResetTargeting();
  }
};

/**
 * Helper function to get units in a specific zone
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user ID
 * @param {string} zoneName - Zone name (e.g., 'opponent_front', 'front', 'flank')
 * @returns {Array} - Array of { unit, zone, index } for units in the zone
 */
function getUnitsInZone(gameState, userId, zoneName) {
  console.log(`🎬 getUnitsInZone: Called with zoneName=${zoneName}, userId=${userId}`);
  
  if (!gameState?.state) {
    console.warn(`🎬 getUnitsInZone: No gameState.state!`);
    return [];
  }
  
  const units = [];
  
  // Determine which player's zone we're targeting
  let targetPlayerId = null;
  let actualZone = zoneName;
  
  const allPlayerIds = Object.keys(gameState.state);
  console.log(`🎬 getUnitsInZone: All player IDs:`, allPlayerIds, `userId: ${userId}`);
  
  if (zoneName.startsWith('opponent_')) {
    // Find opponent's ID
    targetPlayerId = allPlayerIds.find(id => String(id) !== String(userId));
    actualZone = zoneName.replace('opponent_', '');
    console.log(`🎬 getUnitsInZone: Opponent zone detected, targetPlayerId=${targetPlayerId}, actualZone=${actualZone}`);
  } else if (zoneName === 'front' || zoneName === 'flank') {
    // Friendly zone
    targetPlayerId = userId;
    actualZone = zoneName;
  } else if (zoneName.startsWith('your_')) {
    targetPlayerId = userId;
    actualZone = zoneName.replace('your_', '');
  }
  
  if (!targetPlayerId || !gameState.state[targetPlayerId]) {
    console.warn(`🎬 getUnitsInZone: Could not find player for zone ${zoneName}, targetPlayerId=${targetPlayerId}`);
    console.warn(`🎬 getUnitsInZone: Available state keys:`, Object.keys(gameState.state));
    return [];
  }
  
  const playerState = gameState.state[targetPlayerId];
  const playedOrders = playerState.playedOrders || {};
  const zoneOrders = playedOrders[actualZone] || [];
  
  console.log(`🎬 getUnitsInZone: Player ${targetPlayerId} playedOrders zones:`, Object.keys(playedOrders));
  console.log(`🎬 getUnitsInZone: Zone ${actualZone} has ${zoneOrders.length} slots`);
  
  zoneOrders.forEach((unit, index) => {
    if (unit && !unit.markedForRemoval) {
      units.push({
        unit,
        zone: zoneName,
        index,
        instanceId: unit.instanceId
      });
    }
  });
  
  console.log(`🎬 getUnitsInZone: Found ${units.length} units in ${zoneName}`, units.map(u => u.unit?.name));
  return units;
}

export default targetStrategem;
